import { HttpClient } from './http-client';
import { 
  MatchIdentifier,
  MatchIdentifiersListResponse,
  CreateMatchIdentifierRequest,
  UpdateMatchIdentifierRequest,
  TestMatchIdentifierRequest,
  TestMatchIdentifierResult,
  MatchIdentifierTypesResponse,
  ApiError
} from '../types';

export class MatchIdentifierService {
  constructor(private httpClient: HttpClient) {}

  /**
   * Get all match identifiers for a division with optional filtering
   */
  async getMatchIdentifiers(
    divisionId: string,
    params?: {
      active?: boolean;
      search?: string;
      type?: string;
    }
  ): Promise<MatchIdentifiersListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.active !== undefined) {
      searchParams.append('active', params.active.toString());
    }
    
    if (params?.search) {
      searchParams.append('search', params.search);
    }

    if (params?.type) {
      searchParams.append('type', params.type);
    }

    const queryString = searchParams.toString();
    const url = `/api/v1/divisions/${divisionId}/match-identifiers${queryString ? `?${queryString}` : ''}`;
    
    return this.httpClient.get<MatchIdentifiersListResponse>(url);
  }

  /**
   * Get a specific match identifier by ID
   */
  async getMatchIdentifier(divisionId: string, identifierId: string): Promise<MatchIdentifier> {
    return this.httpClient.get<MatchIdentifier>(
      `/api/v1/divisions/${divisionId}/match-identifiers/${identifierId}`
    );
  }

  /**
   * Create a new match identifier
   */
  async createMatchIdentifier(
    divisionId: string, 
    request: CreateMatchIdentifierRequest
  ): Promise<MatchIdentifier> {
    return this.httpClient.post<MatchIdentifier>(
      `/api/v1/divisions/${divisionId}/match-identifiers`, 
      request
    );
  }

  /**
   * Update an existing match identifier
   */
  async updateMatchIdentifier(
    divisionId: string,
    identifierId: string,
    request: UpdateMatchIdentifierRequest
  ): Promise<MatchIdentifier> {
    return this.httpClient.put<MatchIdentifier>(
      `/api/v1/divisions/${divisionId}/match-identifiers/${identifierId}`, 
      request
    );
  }

  /**
   * Deactivate a match identifier (soft delete)
   */
  async deactivateMatchIdentifier(divisionId: string, identifierId: string): Promise<void> {
    return this.httpClient.delete<void>(
      `/api/v1/divisions/${divisionId}/match-identifiers/${identifierId}`
    );
  }

  /**
   * Test a match identifier against sample data
   */
  async testMatchIdentifier(
    divisionId: string,
    request: TestMatchIdentifierRequest
  ): Promise<TestMatchIdentifierResult> {
    return this.httpClient.post<TestMatchIdentifierResult>(
      `/api/v1/divisions/${divisionId}/match-identifiers/test`,
      request
    );
  }

  /**
   * Get available match identifier types and operators
   */
  async getMatchIdentifierTypes(divisionId: string): Promise<MatchIdentifierTypesResponse> {
    return this.httpClient.get<MatchIdentifierTypesResponse>(
      `/api/v1/divisions/${divisionId}/match-identifiers/types`
    );
  }
}
